import camelcaseKeys from 'camelcase-keys';

import { baseApi } from 'api/api-base';
import { API_ROUTES } from 'constants/api-routes';
import {
  GetAutoTopUp,
  GetBillingStatus,
  GetMyStatement,
  GetMyUptime,
  SetupAutoTopUp,
  UpdateAutoTopUp,
  UpdateThreshold,
} from 'types/api/api-billing.types';

// Billing lifecycle for the logged-in customer: dunning/threshold status (drives the
// suspension banner + threshold editor), the monthly itemized statement, and per-device
// uptime. Threshold updates invalidate the status so the editor reflects the saved value.
const taggedBaseApi = baseApi.enhanceEndpoints({ addTagTypes: ['BillingStatus', 'AutoTopUp'] });

export const billingApi = taggedBaseApi.injectEndpoints({
  endpoints: builder => ({
    getBillingStatus: builder.query<GetBillingStatus.ResponseType, GetBillingStatus.RequestType>({
      query: () => ({
        url: API_ROUTES.GET_BILLING_STATUS,
      }),
      providesTags: ['BillingStatus'],
      extraOptions: {},
      transformResponse: (response: GetBillingStatus.RawApiResponse): GetBillingStatus.ResponseType =>
        camelcaseKeys(response as unknown as Record<string, unknown>, {
          deep: true,
        }) as unknown as GetBillingStatus.ResponseType,
    }),
    updateLowBalanceThreshold: builder.mutation<UpdateThreshold.ResponseType, UpdateThreshold.RequestType>({
      query: body => ({
        url: API_ROUTES.UPDATE_LOW_BALANCE_THRESHOLD,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['BillingStatus'],
      extraOptions: {},
    }),
    getMyStatement: builder.query<GetMyStatement.ResponseType, GetMyStatement.RequestType>({
      query: ({ year, month }) => ({
        url: `${API_ROUTES.GET_MY_STATEMENT}?year=${year}&month=${month}`,
      }),
      extraOptions: {},
      transformResponse: (response: GetMyStatement.RawApiResponse): GetMyStatement.ResponseType =>
        camelcaseKeys(response as unknown as Record<string, unknown>, {
          deep: true,
        }) as unknown as GetMyStatement.ResponseType,
    }),
    getAutoTopUp: builder.query<GetAutoTopUp.ResponseType, GetAutoTopUp.RequestType>({
      query: () => ({
        url: API_ROUTES.AUTO_TOPUP,
      }),
      providesTags: ['AutoTopUp'],
      extraOptions: {},
      transformResponse: (response: GetAutoTopUp.RawApiResponse): GetAutoTopUp.ResponseType =>
        camelcaseKeys(response as unknown as Record<string, unknown>, {
          deep: true,
        }) as unknown as GetAutoTopUp.ResponseType,
    }),
    setupAutoTopUp: builder.mutation<SetupAutoTopUp.ResponseType, SetupAutoTopUp.RequestType>({
      query: body => ({
        url: API_ROUTES.AUTO_TOPUP_SETUP,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AutoTopUp'],
      extraOptions: {},
      transformResponse: (response: Record<string, unknown>): SetupAutoTopUp.ResponseType =>
        camelcaseKeys(response, { deep: true }) as unknown as SetupAutoTopUp.ResponseType,
    }),
    updateAutoTopUp: builder.mutation<UpdateAutoTopUp.ResponseType, UpdateAutoTopUp.RequestType>({
      query: body => ({
        url: API_ROUTES.AUTO_TOPUP,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AutoTopUp'],
      extraOptions: {},
      transformResponse: (response: UpdateAutoTopUp.RawApiResponse): UpdateAutoTopUp.ResponseType =>
        camelcaseKeys(response as unknown as Record<string, unknown>, {
          deep: true,
        }) as unknown as UpdateAutoTopUp.ResponseType,
    }),
    getMyUptime: builder.query<GetMyUptime.ResponseType, GetMyUptime.RequestType>({
      query: ({ year, month }) => ({
        url: `${API_ROUTES.GET_MY_UPTIME}?year=${year}&month=${month}`,
      }),
      extraOptions: {},
      transformResponse: (response: GetMyUptime.RawApiResponse): GetMyUptime.ResponseType =>
        camelcaseKeys(response as unknown as Record<string, unknown>, {
          deep: true,
        }) as unknown as GetMyUptime.ResponseType,
    }),
  }),
});

export const {
  useGetAutoTopUpQuery,
  useSetupAutoTopUpMutation,
  useUpdateAutoTopUpMutation,
  useGetBillingStatusQuery,
  useUpdateLowBalanceThresholdMutation,
  useGetMyStatementQuery,
  useGetMyUptimeQuery,
} = billingApi;
